import useEvent from './useEvent';
import type { CargoAllocation, TCOrderEntry } from '../core/Core';
import type { VesselToSortedEvents } from '../core/VesselSchedule';
import type { CargoAllocationOptions } from '../core/suggestions/Suggestions';
import { createNewCargoAllocations } from '../core/Core.cargo-allocation';
import { mapGroupBy } from '@seaber/common/lib/collections';
import type { ScenarioSchedule } from '../Scenario';

// Custom hook for cargo allocation reapplication
export function useReApplyCargoAllocations({
  assignedOrdersById,
  eventsByVessel,
  cargoAllocations,
  allocationOptions,
  saveAllocations,
}: {
  assignedOrdersById: Map<number, TCOrderEntry>;
  eventsByVessel: VesselToSortedEvents;
  cargoAllocations: ScenarioSchedule['cargoAllocations'];
  allocationOptions: CargoAllocationOptions;
  saveAllocations: (allocations: CargoAllocation[]) => void;
}) {
  const allCargoAllocationsByOrderId = mapGroupBy(
    cargoAllocations ?? [],
    (a) => a.orderId,
  );

  return useEvent((orderIds: Array<number>) => {
    if (!allocationOptions.allocateCargo) {
      return;
    }

    // Find a vessel ID for the provided order IDs
    let vesselId = null;
    for (const orderId of orderIds) {
      const vesselIdOfOrder = assignedOrdersById.get(orderId)?.vesselId;
      if (vesselIdOfOrder) {
        vesselId = vesselIdOfOrder;
        break;
      }
    }

    if (!vesselId) {
      return;
    }

    const vesselEvents = eventsByVessel.get(vesselId);

    if (!vesselEvents) {
      return;
    }

    const newCargoAllocations = createNewCargoAllocations(
      vesselEvents,
      orderIds,
      allocationOptions.combineSameAssortment,
    );

    const newCargoAllocationsByOrderId = mapGroupBy(
      newCargoAllocations,
      (allocation) => allocation.orderId,
    );

    const updatedAllocationsByOrderId = new Map(allCargoAllocationsByOrderId);
    for (const [orderId, newAllocations] of newCargoAllocationsByOrderId) {
      const existingAllocations =
        allCargoAllocationsByOrderId.get(orderId) || [];
      const scheduledOrderClientId =
        existingAllocations.at(0)?.scheduledOrderClientId;
      if (scheduledOrderClientId && newAllocations.length) {
        updatedAllocationsByOrderId.set(
          orderId,
          newAllocations.map((allocation) => ({
            ...allocation,
            scheduledOrderClientId,
          })),
        );
      }
    }

    saveAllocations(Array.from(updatedAllocationsByOrderId.values()).flat());
  });
}
